import React from 'react';
import { Plane, Clock, Shield, MapPin, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import ServicePageLayout from './ServicePageLayout';
import { useTranslation } from '../../hooks/useTranslation';

export default function AirportPickup() {
  const { t } = useTranslation();
  
  return (
    <ServicePageLayout
      title={t('services.airportPickup.title')}
      subtitle={t('services.airportPickup.subtitle')}
      icon={Plane}
      heroColor="from-sky-500 to-blue-600"
      enquiryTitle={t('services.airportPickup.title')}
    >
      {/* Main Description */}
      <Card className="mb-8 shadow-lg" data-testid="service-main-card">
        <CardContent className="p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('services.airportPickup.mainTitle')}</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            {t('services.airportPickup.mainDesc1')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('services.airportPickup.mainDesc2')}
          </p>
        </CardContent>
      </Card>

      {/* Features Grid */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-247">
          <CardContent className="p-6">
            <Clock className="w-12 h-12 text-sky-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.airportPickup.features.availability.title')}</h3>
            <p className="text-gray-700">
              {t('services.airportPickup.features.availability.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-safe">
          <CardContent className="p-6">
            <Shield className="w-12 h-12 text-sky-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.airportPickup.features.safe.title')}</h3>
            <p className="text-gray-700">
              {t('services.airportPickup.features.safe.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-door">
          <CardContent className="p-6">
            <MapPin className="w-12 h-12 text-sky-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.airportPickup.features.doorToDoor.title')}</h3>
            <p className="text-gray-700">
              {t('services.airportPickup.features.doorToDoor.desc')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-xl transition-shadow" data-testid="feature-card-flight">
          <CardContent className="p-6">
            <Plane className="w-12 h-12 text-sky-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-3">{t('services.airportPickup.features.flightMonitoring.title')}</h3>
            <p className="text-gray-700">
              {t('services.airportPickup.features.flightMonitoring.desc')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* What's Included */}
      <Card className="mb-8 shadow-lg" data-testid="included-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.whatsIncluded')}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t('services.airportPickup.included').map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Options */}
      <Card className="mb-8 shadow-lg" data-testid="vehicles-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.airportPickup.vehicleOptions')}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-sky-50 rounded-xl p-6 border border-sky-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.airportPickup.sedan.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.airportPickup.sedan.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.airportPickup.sedan.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-sky-50 rounded-xl p-6 border border-sky-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.airportPickup.suv.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.airportPickup.suv.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.airportPickup.suv.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
            <div className="bg-sky-50 rounded-xl p-6 border border-sky-200">
              <h3 className="font-bold text-gray-900 mb-2">{t('services.airportPickup.minivan.title')}</h3>
              <p className="text-gray-700 text-sm mb-4">{t('services.airportPickup.minivan.desc')}</p>
              <ul className="text-sm text-gray-600 space-y-2">
                {t('services.airportPickup.minivan.features').map((feature) => (
                  <li key={feature}>• {feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Airports Covered */}
      <Card className="mb-8 shadow-lg" data-testid="airports-card">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('services.airportPickup.airportsCovered')}</h2>
          <div className="flex flex-wrap gap-3">
            {[
              'Mumbai (BOM)', 'Delhi (DEL)', 'Bangalore (BLR)', 'Chennai (MAA)',
              'Hyderabad (HYD)', 'Kolkata (CCU)', 'Pune (PNQ)', 'Ahmedabad (AMD)',
              'Kochi (COK)', 'Jaipur (JAI)'
            ].map((airport) => (
              <span 
                key={airport} 
                className="px-4 py-2 bg-sky-50 text-sky-700 rounded-full text-sm font-medium border border-sky-200"
              >
                {airport}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </ServicePageLayout>
  );
}
